/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { COUNTRY_TO_CURRENCY_MAP } from '../../../../shared/constants/country-currency.constant';
import { CompanyClient } from '../../../../shared';
import { SubscriptionService } from '../../../../dashboard/services';

declare let bootstrap: any;

@Component({
  selector: 'kt-app-invoice-form-client',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './invoice-form-client.component.html',
  styleUrl: './invoice-form-client.component.scss'
})
export class InvoiceFormClientComponent implements OnChanges, AfterViewInit {
  @Input() invoiceForm!: FormGroup;
  @Input() clients: CompanyClient[] = [];
  @Output() openPremiumModalEvent = new EventEmitter<{ type: 'client' | 'company'; count: number }>();

  @ViewChild('clientSelect') clientSelect!: ElementRef<HTMLSelectElement>;

  private subscriptionService = inject(SubscriptionService);

  canAddClient = this.subscriptionService.canAddClient(this.clients.length);
  showClientForm = signal(false);
  showSaveClient = signal(true);

  selectedClientId = '';

  countryToCurrencyMap: Record<string, string> = COUNTRY_TO_CURRENCY_MAP;

  get countries(): string[] {
    return Object.keys(this.countryToCurrencyMap);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clients']) {
      this.canAddClient =
        this.subscriptionService.canAddClient(this.clients.length);

      this.validateAndUpdateForm();
    }
  }

  private validateAndUpdateForm(): void {
    if (this.clients.length > 0) {
      const client: CompanyClient = this.clients[0];

      this.populateClientForm(client);

      if(client.id)
        this.selectedClientId = client.id.toString();
    }
  }

  onClientSelect(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const clientId = selectElement.value;

    const selectedClient = this.clients.find(c => c.id === clientId);

    if (selectedClient) {
      this.populateClientForm(selectedClient);
      this.showClientForm.set(true);
      this.showSaveClient.set(false);
    } else {
      this.resetClientFields();
      this.showClientForm.set(false);
      this.showSaveClient.set(true);
    }
  }

  private populateClientForm(client: CompanyClient) {
    this.invoiceForm.patchValue({
      clientName: client.name,
      clientAddress: client.address.address,
      clientCity: client.address.city,
      clientProvince: client.address.province,
      clientPostalCode: client.address.postalCode,
      clientCountry: client.address.country,
      saveClientData: false
    });

    this.showClientForm.set(true);
    this.setClientFieldsEnabled(false);
  }

  resetClientFields() {
    this.invoiceForm.patchValue({
      clientName: '',
      clientAddress: '',
      clientCity: '',
      clientProvince: '',
      clientPostalCode: '',
      clientCountry: '',
      saveClientData: this.canAddClient ? true: false
    });

    this.setClientFieldsEnabled(true);
  }

  private setClientFieldsEnabled(enable: boolean): void {
    const companyControls = [
      'clientName',
      'clientAddress',
      'clientCity',
      'clientProvince',
      'clientPostalCode',
      'clientCountry',
      'saveClientData'
    ];

    companyControls.forEach(ctrlName => {
      const control = this.invoiceForm.get(ctrlName);
      if (control) {
        if (enable) {
          control.enable({ emitEvent: false });
        } else {
          control.disable({ emitEvent: false });
        }
      }
    });
  }

  addClientDetails() {
    this.resetClientFields();
    this.selectedClientId = '';
    this.showClientForm.set(true);
    this.showSaveClient.set(true);

    setTimeout(() => {
      if (this.clientSelect?.nativeElement) {
        this.clientSelect.nativeElement.value = '';
      }
    }, 0);
  }

  onPremiumAddClientClick(): void {
    this.openPremiumModalEvent.emit({ type: 'client', count: this.clients.length });  
  }

  ngAfterViewInit() {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach(el => {
      new bootstrap.Tooltip(el);
    });
  }
}
